import { useEffect, useRef, useState } from 'react';
import { useTheme } from '@mui/material/styles';
import {
    Avatar,
    Box,
    Chip,
    ClickAwayListener,
    Divider,
    List,
    ListItemButton,
    ListItemIcon,
    ListItemText,
    Paper,
    Popper,
    Stack,
    Typography
} from '@mui/material';
import { FormattedMessage } from 'react-intl';
import PerfectScrollbar from 'react-perfect-scrollbar';
import MainCard from 'ui-component/cards/MainCard';
import Transitions from 'ui-component/extended/Transitions';
import { IconLogout, IconSettings } from '@tabler/icons';
import useConfig from 'hooks/useConfig';
import { useKeycloak } from '@react-keycloak/web';
import { useUsersContext } from 'hooks/api/users/context';
import { ACTIONS } from 'hooks/api/users/reducer';
import secureLocalStorage from 'react-secure-storage';
import { globalColor } from 'views/ConstantColors';
import { useNavigate } from 'react-router-dom';

const ProfileSection = () => {
    const theme = useTheme();
    const { borderRadius } = useConfig();
    const { keycloak } = useKeycloak();
    const [, dispatch] = useUsersContext();
    const navigate = useNavigate();

    const [selectedIndex, setSelectedIndex] = useState(-1);
    const [open, setOpen] = useState(false);
    const [greeting, setGreeting] = useState('');
    const anchorRef = useRef(null);

    const getGreeting = () => {
        const currentHour = new Date().getHours();
        if (currentHour < 12) return 'Good Morning,';
        if (currentHour < 18) return 'Good Afternoon,';
        return 'Good Evening,';
    };

    useEffect(() => {
        setGreeting(getGreeting());
    }, []);

    const handleClose = (event) => {
        if (anchorRef.current && anchorRef.current.contains(event.target)) {
            return;
        }
        setOpen(false);
    };

    const handleToggle = () => {
        setOpen((prevOpen) => !prevOpen);
    };

    const handleLogout = () => {
        secureLocalStorage.clear();
        dispatch({ type: ACTIONS.SET_USER_ROLES, payload: [] });
        keycloak.logout();
    };

    const prevOpen = useRef(open);
    useEffect(() => {
        if (prevOpen.current === true && open === false) {
            anchorRef.current.focus();
        }
        prevOpen.current = open;
    }, [open]);

    const handleListItemClick = (event, index, route = '') => {
        setSelectedIndex(index);
        handleClose(event);

        if (route && route !== '') {
            navigate(route);
        }
    };

    return (
        <>
            <Chip
                sx={{
                    height: { xs: '40px', sm: '48px' },
                    alignItems: 'center',
                    borderRadius: '27px',
                    transition: 'all .2s ease-in-out',
                    borderColor: globalColor,
                    backgroundColor: globalColor,
                    color: '#ffffff',
                    '&[aria-controls="menu-list-grow"], &:hover': {
                        borderColor: 'rgb(0, 100, 0)',
                        background: 'rgb(0, 100, 0) !important',
                        color: '#ffffff',
                        '& svg': { stroke: '#ffffff' }
                    },
                    '& .MuiChip-label': { lineHeight: 0, paddingLeft: { xs: '8px', sm: '12px' }, paddingRight: { xs: '8px', sm: '12px' } }
                }}
                icon={
                    <Avatar
                        src=""
                        sx={{
                            ...theme.typography.mediumAvatar,
                            width: { xs: '32px', sm: '34px' },
                            height: { xs: '32px', sm: '34px' },
                            margin: { xs: '4px 0 4px 4px !important', sm: '8px 0 8px 8px !important' },
                            cursor: 'pointer'
                        }}
                        ref={anchorRef}
                        aria-controls={open ? 'menu-list-grow' : undefined}
                        aria-haspopup="true"
                        color="inherit"
                        alt="user-account"
                    />
                }
                label={<IconSettings stroke={1.5} size={theme.breakpoints.down('sm') ? '20px' : '24px'} color="#ffffff" />}
                variant="outlined"
                ref={anchorRef}
                aria-controls={open ? 'menu-list-grow' : undefined}
                aria-haspopup="true"
                onClick={handleToggle}
                color="primary"
            />

            <Popper
                placement="bottom"
                open={open}
                anchorEl={anchorRef.current}
                role={undefined}
                transition
                disablePortal
                modifiers={[
                    {
                        name: 'offset',
                        options: { offset: [0, 14] }
                    }
                ]}
            >
                {({ TransitionProps }) => (
                    <ClickAwayListener onClickAway={handleClose}>
                        <Transitions in={open} {...TransitionProps}>
                            <Paper>
                                {open && (
                                    <MainCard border={false} elevation={16} content={false} boxShadow shadow={theme.shadows[16]}>
                                        <Box sx={{ p: 2, pb: 0 }}>
                                            {!!keycloak.authenticated && (
                                                <Stack>
                                                    <Stack direction="row" spacing={0.5} alignItems="center">
                                                        <Typography variant="h4">{greeting}</Typography>
                                                        <Typography component="span" variant="h4" sx={{ fontWeight: 400 }}>
                                                            {keycloak?.tokenParsed?.name}
                                                        </Typography>
                                                    </Stack>
                                                    <Typography variant="subtitle2">{keycloak?.tokenParsed?.preferred_username}</Typography>
                                                </Stack>
                                            )}
                                        </Box>
                                        <PerfectScrollbar
                                            style={{
                                                height: '100%',
                                                maxHeight: 'calc(100vh - 250px)',
                                                overflowX: 'hidden'
                                            }}
                                        >
                                            <Box sx={{ p: 2, pt: 0 }}>
                                                <List
                                                    component="nav"
                                                    sx={{
                                                        width: '100%',
                                                        maxWidth: 350,
                                                        minWidth: 300,
                                                        backgroundColor: theme.palette.background.paper,
                                                        borderRadius: '10px',
                                                        [theme.breakpoints.down('md')]: {
                                                            minWidth: '100%'
                                                        },
                                                        '& .MuiListItemButton-root': {
                                                            mt: 0.5
                                                        }
                                                    }}
                                                >
                                                    <ListItemButton
                                                        sx={{ borderRadius: `${borderRadius}px` }}
                                                        selected={selectedIndex === 0}
                                                        onClick={(event) => handleListItemClick(event, 0, '/settings')}
                                                    >
                                                        <ListItemIcon>
                                                            <IconSettings stroke={1.5} size="20px" />
                                                        </ListItemIcon>
                                                        <ListItemText
                                                            primary={
                                                                <Typography variant="body2">
                                                                    <FormattedMessage id="settings" />
                                                                </Typography>
                                                            }
                                                        />
                                                    </ListItemButton>
                                                </List>
                                                <Divider />
                                                <List
                                                    component="nav"
                                                    sx={{
                                                        width: '100%',
                                                        maxWidth: 350,
                                                        minWidth: 300,
                                                        backgroundColor: theme.palette.background.paper,
                                                        borderRadius: '10px',
                                                        [theme.breakpoints.down('md')]: {
                                                            minWidth: '100%'
                                                        },
                                                        '& .MuiListItemButton-root': {
                                                            mt: 0.5
                                                        }
                                                    }}
                                                >
                                                    {!!keycloak.authenticated && (
                                                        <ListItemButton
                                                            sx={{ borderRadius: `${borderRadius}px` }}
                                                            selected={selectedIndex === 1}
                                                            onClick={handleLogout}
                                                        >
                                                            <ListItemIcon>
                                                                <IconLogout stroke={1.5} size="20px" />
                                                            </ListItemIcon>
                                                            <ListItemText
                                                                primary={
                                                                    <Typography variant="body2">
                                                                        <FormattedMessage id="logout" />
                                                                    </Typography>
                                                                }
                                                            />
                                                        </ListItemButton>
                                                    )}
                                                </List>
                                            </Box>
                                        </PerfectScrollbar>
                                    </MainCard>
                                )}
                            </Paper>
                        </Transitions>
                    </ClickAwayListener>
                )}
            </Popper>
        </>
    );
};

export default ProfileSection;
